'use client';

import { useState } from 'react';
import { RotateCcw, BookOpen, Clock, AlertTriangle, CheckCircle, UserPlus } from 'lucide-react';
import { BorrowRecordCard } from '@/components/borrow-record-card';
import { Header } from '@/components/header';
import { borrowRecords, users, books } from '@/lib/data';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function BorrowPage() {
  const [activeTab, setActiveTab] = useState('all');
  const [isBorrowDialogOpen, setIsBorrowDialogOpen] = useState(false);
  const [isReturnDialogOpen, setIsReturnDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<string | null>(null);

  const allRecords = borrowRecords;
  const activeRecords = borrowRecords.filter(r => r.status === 'borrowed');
  const overdueRecords = borrowRecords.filter(r => r.status === 'overdue');
  const returnedRecords = borrowRecords.filter(r => r.status === 'returned');

  const getRecordsByTab = () => {
    switch (activeTab) {
      case 'active': return activeRecords;
      case 'overdue': return overdueRecords;
      case 'returned': return returnedRecords;
      default: return allRecords;
    }
  };

  const handleReturn = (record: typeof borrowRecords[0]) => {
    setSelectedRecord(record.id);
    setIsReturnDialogOpen(true);
  };

  const handleRenew = (record: typeof borrowRecords[0]) => {
    console.log('Renew:', record);
  };

  return (
    <div className="min-h-screen bg-[#0A192F]">
      <Header 
        title="借阅管理" 
        subtitle="管理图书借阅、归还及续借"
      />

      <div className="p-8">
        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="p-6 rounded-xl bg-gradient-to-br from-[#3B82F6]/20 to-[#8B5CF6]/20 border border-[#3B82F6]/30">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#3B82F6]/20 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-[#3B82F6]" />
              </div>
              <div>
                <p className="text-sm text-[#8892B0]">当前借阅中</p>
                <p className="text-2xl font-bold text-[#E6F1FF]">{activeRecords.length}</p>
              </div>
            </div>
          </div>

          <div className="p-6 rounded-xl bg-gradient-to-br from-[#EF4444]/20 to-[#F59E0B]/20 border border-[#EF4444]/30">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#EF4444]/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-[#EF4444]" />
              </div>
              <div>
                <p className="text-sm text-[#8892B0]">逾期未还</p>
                <p className="text-2xl font-bold text-[#E6F1FF]">{overdueRecords.length}</p>
              </div>
            </div>
          </div>

          <div className="p-6 rounded-xl bg-gradient-to-br from-[#10B981]/20 to-[#64FFDA]/20 border border-[#10B981]/30">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#10B981]/20 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-[#10B981]" />
              </div>
              <div>
                <p className="text-sm text-[#8892B0]">本月归还</p>
                <p className="text-2xl font-bold text-[#E6F1FF]">{returnedRecords.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex items-center justify-between">
            <TabsList className="bg-[#112240] border border-[#1d3557]">
              <TabsTrigger value="all" className="data-[state=active]:bg-[#1d3557] data-[state=active]:text-[#64FFDA]">
                全部记录
                <Badge className="ml-2 bg-[#3B82F6]/20 text-[#3B82F6] border-0">{allRecords.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="active" className="data-[state=active]:bg-[#1d3557] data-[state=active]:text-[#64FFDA]">
                借阅中
                <Badge className="ml-2 bg-[#3B82F6]/20 text-[#3B82F6] border-0">{activeRecords.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="overdue" className="data-[state=active]:bg-[#1d3557] data-[state=active]:text-[#64FFDA]">
                已逾期
                <Badge className="ml-2 bg-[#EF4444]/20 text-[#EF4444] border-0">{overdueRecords.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="returned" className="data-[state=active]:bg-[#1d3557] data-[state=active]:text-[#64FFDA]">
                已归还
                <Badge className="ml-2 bg-[#10B981]/20 text-[#10B981] border-0">{returnedRecords.length}</Badge>
              </TabsTrigger>
            </TabsList>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="border-[#1d3557] text-[#8892B0] hover:text-[#E6F1FF] hover:bg-[#1d3557]"
                onClick={() => setIsReturnDialogOpen(true)}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                快速归还
              </Button>
              <Button
                className="bg-[#64FFDA] text-[#0A192F] hover:bg-[#64FFDA]/90"
                onClick={() => setIsBorrowDialogOpen(true)}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                办理借阅
              </Button>
            </div>
          </div>

          <TabsContent value={activeTab} className="space-y-3">
            {getRecordsByTab().map((record) => (
              <BorrowRecordCard
                key={record.id}
                record={record}
                onReturn={handleReturn}
                onRenew={handleRenew}
              />
            ))}
            {getRecordsByTab().length === 0 && (
              <div className="text-center py-16">
                <Clock className="w-12 h-12 mx-auto text-[#8892B0] mb-4" />
                <p className="text-[#8892B0]">暂无记录</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Borrow Dialog */}
      <Dialog open={isBorrowDialogOpen} onOpenChange={setIsBorrowDialogOpen}>
        <DialogContent className="bg-[#112240] border-[#1d3557] text-[#E6F1FF] max-w-lg">
          <DialogHeader>
            <DialogTitle>办理借阅</DialogTitle>
            <DialogDescription className="text-[#8892B0]">
              选择用户和图书完成借阅登记
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>选择用户</Label>
              <Select>
                <SelectTrigger className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]">
                  <SelectValue placeholder="搜索或选择用户" />
                </SelectTrigger>
                <SelectContent className="bg-[#112240] border-[#1d3557]">
                  {users.filter(u => u.status === 'active').map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name} ({user.borrowedBooks}/{user.maxBooks}本)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>选择图书</Label>
              <Select>
                <SelectTrigger className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]">
                  <SelectValue placeholder="搜索或扫描图书" />
                </SelectTrigger>
                <SelectContent className="bg-[#112240] border-[#1d3557]">
                  {books.filter(b => b.status === 'available').map((book) => (
                    <SelectItem key={book.id} value={book.id}>
                      {book.title} - {book.author}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>借阅期限</Label>
              <Select defaultValue="30">
                <SelectTrigger className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#112240] border-[#1d3557]">
                  <SelectItem value="14">14天</SelectItem>
                  <SelectItem value="30">30天</SelectItem>
                  <SelectItem value="60">60天</SelectItem>
                  <SelectItem value="90">90天</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" className="border-[#1d3557] text-[#8892B0]" onClick={() => setIsBorrowDialogOpen(false)}>
              取消
            </Button>
            <Button className="bg-[#64FFDA] text-[#0A192F] hover:bg-[#64FFDA]/90">
              确认借阅
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Return Dialog */}
      <Dialog open={isReturnDialogOpen} onOpenChange={setIsReturnDialogOpen}>
        <DialogContent className="bg-[#112240] border-[#1d3557] text-[#E6F1FF] max-w-lg">
          <DialogHeader>
            <DialogTitle>办理归还</DialogTitle>
            <DialogDescription className="text-[#8892B0]">
              扫描图书或输入借阅记录编号
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>图书ISBN / 借阅编号</Label>
              <Input 
                className="bg-[#0A192F] border-[#1d3557] text-[#E6F1FF]" 
                placeholder="扫描或输入..."
              />
            </div>
            <div className="p-4 rounded-lg bg-[#0A192F] border border-[#1d3557]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#10B981]" />
                </div>
                <div>
                  <p className="text-sm text-[#E6F1FF]">图书状态检查</p>
                  <p className="text-xs text-[#8892B0]">系统将自动计算逾期罚款</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" className="border-[#1d3557] text-[#8892B0]" onClick={() => setIsReturnDialogOpen(false)}>
              取消
            </Button>
            <Button className="bg-[#64FFDA] text-[#0A192F] hover:bg-[#64FFDA]/90">
              确认归还
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
